import React, { Component } from 'react';
import { SizeMe } from 'react-sizeme';
import PropTypes from 'prop-types';
import reduxForm from 'redux-form/lib/reduxForm';

import Col from 'react-bootstrap/lib/Col';
import Row from 'react-bootstrap/lib/Row';
import Form from 'react-bootstrap/lib/Form';
import Alert from 'react-bootstrap/lib/Alert';
import Button from 'react-bootstrap/lib/Button';
import FormGroup from 'react-bootstrap/lib/FormGroup';
import AsyncTypeahead from 'react-bootstrap-typeahead/lib/AsyncTypeahead.react';

import If from '../../../../shared/If';
import Box from '../../../../shared/Box';
import Text from '../../../../shared/Text';
import Heading from '../../../../shared/Heading';
import Spinner from '../../../../shared/Spinner';

import OutboundLink from '../../../../components/OutboundLink';
import PodcastSquare from '../../../../components/PodcastSquare';
import { CenteredSpinner } from '../../../../components/Spinner';
import styles from '../../../../components/OnboardingRouter/styles.sass';

import {
  ONBOARDING_IMPORT_RSS_FORM,
  PODCAST_IMPORT_SUPPORT_LINK,
} from '../../../../onboarding';

const ImportForm = ({
  handleSubmit,
  isSearching,
  onSelectTypeahead,
  onTypeaheadSearch,
  rssTypeaheadResults,
  renderTypeaheadOption,
  rssFeedError,
  hasDataToContinue,
  fetchingRSSFeedImport,
  rssFeedIsValid,
  onClickLetsDoIt,
  rssFeedMetadata,
}) => (
  <Box paddingBottom={57}>
    <Form onSubmit={handleSubmit}>
      <FormGroup id="podcastName" bsClass={styles.podcastRSSField}>
        <SizeMe>
          {({ size }) => (
            <AsyncTypeahead
              autoFocus
              caseSensitive
              emptyLabel="No podcasts or RSS feeds found."
              isLoading={isSearching}
              labelKey={option => option.podcastName}
              filterBy={() => true}
              onChange={onSelectTypeahead}
              onSearch={onTypeaheadSearch}
              options={isSearching ? [] : rssTypeaheadResults}
              placeholder={
                size.width < 500
                  ? 'Enter podcast title / RSS'
                  : 'Type in the name of your podcast OR paste in your RSS feed'
              }
              renderMenuItemChildren={renderTypeaheadOption}
            />
          )}
        </SizeMe>
        {rssFeedError && rssFeedError.message && (
          <Alert bsStyle="danger">
            <Text color="#ffffff" size="sm" align="center">
              {rssFeedError.message}
            </Text>
          </Alert>
        )}
        {hasDataToContinue && !rssFeedIsValid && rssFeedError.message && (
          <Alert bsStyle="danger">
            <Text color="#ffffff" size="sm" align="center">
              Feed is not valid
            </Text>
          </Alert>
        )}
      </FormGroup>
      <Box marginTop={7}>
        <Text size="sm" align="center" color="#ffffff">
          (Switching only takes 5 minutes. Seriously!)
        </Text>
      </Box>
      <If
        condition={hasDataToContinue}
        ifRender={() => (
          <Box display="flex" alignItems="center" justifyContent="around" wrap>
            <Box
              maxWidth={700}
              width="100%"
              display="flex"
              color="rgba(41, 47, 54, 0.3)"
              padding={25}
              marginTop={22}
              shape="rounded"
              wrap
              justifyContent="center"
            >
              <Box marginRight={24} marginBottom={16}>
                <PodcastSquare
                  borderRadius={10}
                  image={rssFeedMetadata.image}
                  size="88px"
                  titleSize="1.2vw"
                />
              </Box>
              <Box maxWidth={533} width="100%">
                <Heading size="xs" color="#ffffff" isBold>
                  {rssFeedMetadata.podcastName}
                </Heading>
                <Box marginBottom={10}>
                  <Text size="md" color="#ffffff">
                    {rssFeedMetadata.podcastDescription}
                  </Text>
                </Box>
                <Text size="md" color="#ffffff">
                  {rssFeedMetadata.episodeCount === 1
                    ? `${rssFeedMetadata.episodeCount} episode found`
                    : `${rssFeedMetadata.episodeCount} episodes found`}
                </Text>

                <i />
              </Box>
            </Box>
            <Box maxWidth={220} width="100%" marginTop={17}>
              <button
                onClick={e => {
                  e.preventDefault();
                  onClickLetsDoIt();
                }}
                className={`cyanButton ${styles.letsDoItButton}`}
              >
                Let's do it
              </button>
            </Box>
          </Box>
        )}
      />
      <If
        condition={fetchingRSSFeedImport}
        ifRender={() => (
          <Box
            width="100%"
            display="flex"
            justifyContent="center"
            marginTop={10}
            marginBottom={10}
          >
            <Spinner color="#ffffff" />
          </Box>
        )}
      />
    </Form>
  </Box>
);

const ImportReduxForm = reduxForm({
  form: ONBOARDING_IMPORT_RSS_FORM,
  validate: values => {
    if (values.rssFeed) {
      return {};
    }
    return {
      _error: 'A feed is required',
      rssFeed: 'Required',
    };
  },
})(ImportForm);

ImportForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  isSearching: PropTypes.bool.isRequired,
  onSelectTypeahead: PropTypes.arrayOf(
    PropTypes.shape({
      feedUrl: PropTypes.string.isRequired,
      podcastName: PropTypes.string.isRequired,
    }).isRequired
  ).isRequired,
  onTypeaheadSearch: PropTypes.func.isRequired,
  rssTypeaheadResults: PropTypes.arrayOf(
    PropTypes.shape({
      podcastName: PropTypes.string.isRequired,
      podcastDescription: PropTypes.string.isRequired,
      authorName: PropTypes.string.isRequired,
      episodeCount: PropTypes.number.isRequired,
      image: PropTypes.string.isRequired,
      feedUrl: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    }).isRequired
  ).isRequired,
  renderTypeaheadOption: PropTypes.func.isRequired,
  rssFeedError: PropTypes.shape({
    message: PropTypes.string.isRequired,
  }).isRequired,
  hasDataToContinue: PropTypes.bool.isRequired,
  fetchingRSSFeedImport: PropTypes.bool.isRequired,
  rssFeedIsValid: PropTypes.bool.isRequired,
  onClickLetsDoIt: PropTypes.func.isRequired,
  rssFeedMetadata: PropTypes.shape({
    authorName: PropTypes.string.isRequired,
    authorEmail: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    isExplicit: PropTypes.bool.isRequired,
    itunesCategory: PropTypes.string.isRequired,
    language: PropTypes.string.isRequired,
    podcastName: PropTypes.string.isRequired,
    podcastDescription: PropTypes.string.isRequired,
    episodeCount: PropTypes.number.isRequired,
  }).isRequired,
};

export default ImportReduxForm;
